import { useEffect, useRef, useState } from "react";
import type { GuessRow, Mark } from "@/lib/types";
import { cn } from "@/lib/utils";

const TONE: Record<Mark, string> = {
  hit: "tile-hit",
  near: "tile-near",
  miss: "tile-miss",
};

/** Time between one tile turning over and the next one starting. */
const FLIP_STAGGER = 260;
const FLIP_HALF = 250;

type Props = {
  rows: GuessRow[];
  /** What the player has typed but not submitted. */
  draft: string;
  wordLength: number;
  maxGuesses: number;
  /** Row the server just marked, or null. Drives the reveal. */
  revealIndex: number | null;
  /** Bumped on every rejected guess so the draft row can shake again. */
  shakeSeq: number;
  solved: boolean;
};

export function Board({
  rows,
  draft,
  wordLength,
  maxGuesses,
  revealIndex,
  shakeSeq,
  solved,
}: Props) {
  /**
   * How much of the freshly marked row has turned over. Colour is only painted
   * once a tile is edge-on, which is what sells the flip.
   */
  const [revealed, setRevealed] = useState(0);
  const [bounce, setBounce] = useState(false);
  const [shaking, setShaking] = useState(false);
  const firstShake = useRef(shakeSeq);

  useEffect(() => {
    if (revealIndex === null) {
      setRevealed(0);
      return;
    }
    setRevealed(0);
    setBounce(false);
    const timers = Array.from({ length: wordLength }, (_, i) =>
      setTimeout(() => setRevealed(i + 1), i * FLIP_STAGGER + FLIP_HALF),
    );
    return () => timers.forEach(clearTimeout);
  }, [revealIndex, wordLength]);

  // The win dance waits for the last tile to land.
  useEffect(() => {
    if (!solved || revealIndex === null) return;
    const timer = setTimeout(() => setBounce(true), wordLength * FLIP_STAGGER + 200);
    return () => clearTimeout(timer);
  }, [solved, revealIndex, wordLength]);

  useEffect(() => {
    if (shakeSeq === firstShake.current) return;
    setShaking(true);
    const timer = setTimeout(() => setShaking(false), 620);
    return () => clearTimeout(timer);
  }, [shakeSeq]);

  if (wordLength === 0) return null;

  const draftRow = rows.length;
  /** Nine rows of eight need to breathe less than five rows of five. */
  const gap = wordLength >= 7 || maxGuesses >= 8 ? 4 : 6;
  const tileCap = maxGuesses >= 8 ? 60 : 68;

  return (
    <div
      /**
       * The board is sized by whichever runs out first, width or height: it
       * keeps its own aspect so nine rows of eight still fit on a laptop
       * without the page scrolling.
       */
      className="mx-auto grid h-full w-full"
      style={{
        gap,
        gridTemplateRows: `repeat(${maxGuesses}, minmax(0, 1fr))`,
        aspectRatio: `${wordLength} / ${maxGuesses}`,
        maxWidth: Math.min(wordLength * tileCap, 520),
        maxHeight: "100%",
        width: "auto",
      }}
    >
      {Array.from({ length: maxGuesses }, (_, r) => {
        const row = rows[r];
        const isDraft = r === draftRow;
        const revealing = r === revealIndex;

        return (
          <div
            key={r}
            className={cn("grid", isDraft && shaking && "row-shake")}
            style={{
              gap,
              gridTemplateColumns: `repeat(${wordLength}, minmax(0, 1fr))`,
            }}
          >
            {Array.from({ length: wordLength }, (_, c) => {
              if (row) {
                const settled = !revealing || c < revealed;
                return (
                  <span
                    key={c}
                    className={cn(
                      "tile aspect-auto h-full w-full text-[clamp(15px,min(6vw,4.6vh),30px)]",
                      settled ? TONE[row.marks[c]!] : "tile-filled",
                      settled && row.wild && "tile-wild",
                      revealing && "tile-flip",
                      bounce && solved && r === revealIndex && "tile-bounce",
                    )}
                    style={{
                      animationDelay: revealing
                        ? `${c * FLIP_STAGGER}ms`
                        : bounce
                          ? `${c * 100}ms`
                          : undefined,
                    }}
                  >
                    {row.word[c]}
                  </span>
                );
              }

              const letter = isDraft ? draft[c] : undefined;
              return (
                <span
                  key={c}
                  className={cn(
                    "tile aspect-auto h-full w-full text-[clamp(15px,min(6vw,4.6vh),30px)]",
                    letter ? "tile-filled" : "tile-empty",
                  )}
                >
                  {letter ?? ""}
                </span>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
